// react
import { useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate } from '@tanstack/react-router';

// material ui icons
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';

// material ui
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGridPremium,
  GridRowEditStopReasons,
} from '@mui/x-data-grid-premium';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';
import RowActions from '@/components/ui/datagrid/RowActions';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import DeleteGatewayDialog from './DeleteGatewayDialog';
import GatewayStatusCell from './GatewayStatusCell';

const countRecent = (activities) => {
  const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
  return (
    activities?.filter((a) => new Date(a.LastActivity).getTime() >= oneDayAgo)
      .length ?? 0
  );
};

const GatewayDataGrid = (props) => {
  const { t } = useTranslation('units-gateways-sensors');
  const { gateways, isLoading } = props;
  const dispatch = useDispatch();

  const { isAdmin } = useSessionAuth();

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gatewayToDelete, setGatewayToDelete] = useState(null);

  const rows =
    gateways?.map((gateway) => {
      const LastActivity = new Date(gateway.LastActivity);

      const ConnectedSensors = countRecent(gateway?.SensorActivities);
      const ExternalConnectedSensors = countRecent(
        gateway?.ExternalSensorActivities,
      );
      const connections = `${ConnectedSensors === 0 ? 'No' : ConnectedSensors} sensors, ${ExternalConnectedSensors === 0 ? 'No' : ExternalConnectedSensors} external sensors`;

      return {
        id: gateway?.GatewayId,
        GatewayId: gateway?.GatewayId,
        Name: gateway?.Name,
        Online: gateway?.Online,
        ConnectionStatus: gateway?.ConnectionStatus,
        Connections: connections,
        LastActivity: isNaN(LastActivity.getTime()) ? null : LastActivity,
        CurrentApplicationFirmware: gateway?.CurrentApplicationFirmware,
        ReportedApplicationFirmware: gateway?.ReportedApplicationFirmware,
        SoftwareVersion: gateway?.SWRevision,
        HardwareVersion: gateway?.HWRevision,
        GatewayVersion: gateway?.GatewayVersion,
        Modem: gateway?.Modem,
        PCT: gateway?.PCT,
        OptoEye: gateway?.OptoEye,
      };
    }) ?? [];

  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);

  const navigate = useNavigate();

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    // Note: Since rows are derived from gateways prop, actual updates should be handled
    // by calling API mutations and letting RTK Query invalidate and refetch data
    console.log('Row update requested:', updatedRow);
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleViewGateway = (params) => {
    if (selectedRow && selectedRow.id === params.row.id) {
      setSelectedRow(null);
    } else {
      setSelectedRow(params.row);
      navigate({ to: `/$accountId/gateways/${params.id}` });
    }
  };

  const getRowClassName = (params) => {
    return selectedRow && selectedRow.id === params.id ? 'selected-row' : '';
  };

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    'Gateways-DataGrid-Presets',
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {
            GatewayVersion: false,
            SoftwareVersion: false,
            HardwareVersion: false,
            Capability: false,
            ReportedApplicationFirmware: false,
            LastActivity: false,
          },
          density: 'standard',
        },
      ],
      chosenPreset: 0,
    },
  );

  const handleVisibilityChange = (model) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, visibilityList: model } : p,
      ),
    }));
  };

  const handleDensityChange = (density) => {
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((p, i) =>
        i === prev.chosenPreset ? { ...p, density } : p,
      ),
    }));
  };

  const handleCopyGatewayId = async (gateway) => {
    if (!gateway?.GatewayId) return;
    try {
      await navigator.clipboard.writeText(gateway.GatewayId);
      dispatch(
        showSnackbar({
          message:
            t?.('gateways.gatewayIdCopied', { id: gateway.GatewayId }) ||
            `Gateway ID copied: ${gateway.GatewayId}`,
          severity: 'success',
        }),
      );
    } catch (err) {
      console.error('Failed to copy gateway ID:', err);
      dispatch(
        showSnackbar({
          message:
            t?.('gateways.gatewayIdCopyFailed') || 'Failed to copy Gateway ID',
          severity: 'error',
        }),
      );
    }
  };

  const handleDeleteGateway = (gateway) => {
    setGatewayToDelete(gateway);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setGatewayToDelete(null);
  };

  const handleEditGateway = (params) => {
    if (selectedRow && selectedRow.id === params.row.id) {
      setSelectedRow(null);
    } else {
      setSelectedRow(params.row);
      navigate({ to: `/$accountId/gateways/${params.id}/edit` });
    }
  };

  // Admin-only columns
  const adminColumns = [
    {
      field: 'GatewayVersion',
      headerName: 'Version',
      flex: 0.5,
    },
    {
      field: 'SoftwareVersion',
      headerName: 'SW Version',
      flex: 0.5,
    },
    {
      field: 'HardwareVersion',
      headerName: 'HW Version',
      flex: 0.5,
    },
    {
      field: 'Capability',
      headerName: t('gateways.capability'),
      flex: 0.75,
      renderCell: (params) => {
        const capabilities = [];
        if (params.row.Modem) capabilities.push('Modem');
        if (params.row.PCT) capabilities.push('PCT');
        if (params.row.OptoEye) capabilities.push('OptoEye');

        return (
          <Box display="flex" alignItems="center" height="100%">
            <Typography variant="body2">
              {capabilities.length > 0 ? capabilities.join(', ') : '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'ReportedApplicationFirmware',
      headerName: t('gateways.firmware'),
      flex: 0.5,
      type: 'number',
      renderCell: (params) => {
        const currentFW = params.row.CurrentApplicationFirmware;
        const reportedFW = params.row.ReportedApplicationFirmware;

        return (
          <Tooltip
            title={
              reportedFW
                ? t('gateways.reportedFirmwareTooltip', 'Reported Firmware')
                : t(
                    'gateways.currentFirmwareTooltip',
                    'Current Firmware (Deprecated)',
                  )
            }
            placement="left"
          >
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2">
                {reportedFW ? `${reportedFW} ` : `${currentFW}`}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const columns = [
    {
      field: 'Online',
      headerName: t('gateways.status'),
      width: 120,
      renderCell: (params) => <GatewayStatusCell gateway={params.row} />,
    },
    {
      field: 'GatewayId',
      headerName: t('gateways.gatewayId'),
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              height: '100%',
              width: '100%',
              '& .copy-button': {
                opacity: 0,
                transition: 'opacity 0.2s ease',
              },
              '&:hover .copy-button': {
                opacity: 1,
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params?.row?.GatewayId || t('gateways.notAvailable')}
            </Typography>
            <IconButton
              className="copy-button"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyGatewayId(params.row);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    },
    { field: 'Name', headerName: t('gateways.name'), flex: 1 },
    // Conditionally include admin columns
    ...(isAdmin ? adminColumns : []),
    {
      field: 'LastActivity',
      headerName: t('gateways.lastActivity'),
      flex: 0.5,
      groupable: false,
      type: 'dateTime',
      valueFormatter: (value) => {
        if (!value) return '-';
        return value.toLocaleString('da-DK', {
          timeZone: 'Europe/Copenhagen',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    },
    {
      field: 'Connections',
      headerName: t('gateways.connections'),
      flex: 1,
      renderCell: (params) => (
        <div>
          {(params.value || '').split(',').map((v, i) => (
            <Chip key={i} label={v.trim()} size="small" sx={{ mr: 0.5 }} />
          ))}
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: t('gateways.actions'),
      width: 120,
      sortable: false,
      filterable: false,
      resizable: false,
      groupable: false,
      hideable: false,
      renderCell: (params) => (
        <RowActions
          row={params.row}
          onView={() => handleViewGateway(params.row)}
          onEdit={() => handleEditGateway(params.row)}
          onDelete={() => handleDeleteGateway(params.row)}
        />
      ),
    },
  ];

  const localeText = useDatagridLocaleText();

  return (
    <>
      <Box data-tour="gateway-overview-grid">
        <DataGridPremium
          showToolbar
          rows={rows}
          columns={columns}
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          rowsName="Gateways"
          localeText={localeText}
          getRowClassName={getRowClassName}
          isLoading={isLoading}
          density={dataGridState.presets[dataGridState.chosenPreset].density}
          onDensityChange={handleDensityChange}
          onColumnVisibilityModelChange={handleVisibilityChange}
          columnVisibilityModel={{
            ...dataGridState.presets[dataGridState.chosenPreset].visibilityList,
          }}
          slots={{
            toolbar: DataGridToolbar,
          }}
          slotProps={{
            toolbar: {
              showExport: true,
              showFilters: true,
              showColumns: true,
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>

      <DeleteGatewayDialog
        open={deleteDialogOpen}
        gateway={gatewayToDelete}
        onClose={handleDeleteDialogClose}
      />
    </>
  );
};

export default GatewayDataGrid;
